import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

const api =
    new API();


/*
|--------------------------------------------------------------------------
| DEFAULTS
|--------------------------------------------------------------------------
*/

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
};

const DEFAULT_SUMMARY = {
    totalSuppliers: 0,
    activeSuppliers: 0,
    inactiveSuppliers: 0,
    totalOpeningBalance: 0,
};


/*
|--------------------------------------------------------------------------
| ERROR
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback =
        "Something went wrong."
) => {
    if (
        typeof error ===
        "string"
    ) {
        return error;
    }

    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};


/*
|--------------------------------------------------------------------------
| NORMALIZE API RESPONSE
|--------------------------------------------------------------------------
*/

const getResponseEnvelope = (
    response
) => {
    if (
        !response
    ) {
        return {};
    }

    /*
    |--------------------------------------------------------------------------
    | Axios response
    |--------------------------------------------------------------------------
    */

    if (
        response?.data &&
        typeof response.data ===
            "object" &&
        !Array.isArray(
            response.data
        ) &&
        (
            response?.status !==
                undefined ||
            response?.headers !==
                undefined ||
            response?.config !==
                undefined
        )
    ) {
        return response.data;
    }

    /*
    |--------------------------------------------------------------------------
    | API helper already returned backend body
    |--------------------------------------------------------------------------
    */

    return response;
};


/*
|--------------------------------------------------------------------------
| RESPONSE DATA
|--------------------------------------------------------------------------
*/

const getResponseData = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    return (
        envelope?.data ??
        envelope
    );
};


/*
|--------------------------------------------------------------------------
| RECORDS
|--------------------------------------------------------------------------
*/

const getRecords = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    const data =
        envelope?.data ??
        envelope;

    /*
    |--------------------------------------------------------------------------
    | API:
    |
    | {
    |     data: [...]
    | }
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            data
        )
    ) {
        return data;
    }

    /*
    |--------------------------------------------------------------------------
    | API:
    |
    | {
    |     data: {
    |         records: []
    |     }
    | }
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            data?.records
        )
    ) {
        return data.records;
    }

    /*
    |--------------------------------------------------------------------------
    | API:
    |
    | {
    |     records: []
    | }
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            envelope?.records
        )
    ) {
        return envelope.records;
    }

    return [];
};


/*
|--------------------------------------------------------------------------
| PAGINATION
|--------------------------------------------------------------------------
*/

const getPagination = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    const data =
        envelope?.data ??
        {};

    const pagination =
        envelope?.pagination ||
        data?.pagination ||
        {};

    return {
        page:
            Number(
                pagination?.page
            ) || 1,

        limit:
            Number(
                pagination?.limit
            ) || 20,

        total:
            Number(
                pagination?.total
            ) || 0,

        totalPages:
            Math.max(
                Number(
                    pagination
                        ?.totalPages
                ) || 1,
                1
            ),

        hasNextPage:
            Boolean(
                pagination
                    ?.hasNextPage
            ),

        hasPreviousPage:
            Boolean(
                pagination
                    ?.hasPreviousPage
            ),
    };
};


/*
|--------------------------------------------------------------------------
| SUMMARY
|--------------------------------------------------------------------------
*/

const getSummary = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    const data =
        envelope?.data ??
        {};

    return (
        envelope?.summary ||
        data?.summary ||
        {}
    );
};


/*
|--------------------------------------------------------------------------
| QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value ===
                    undefined ||
                value ===
                    null ||
                value ===
                    ""
            ) {
                return;
            }

            query.append(
                key,
                value
            );
        }
    );

    const value =
        query.toString();

    return value
        ? `?${value}`
        : "";
};


/*
|--------------------------------------------------------------------------
| FETCH SUPPLIERS
|--------------------------------------------------------------------------
*/

export const fetchSuppliers =
    createAsyncThunk(
        "supplier/fetchSuppliers",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        search:
                            params.search,

                        status:
                            params.status,

                        branchId:
                            params.branchId,

                        firmId:
                            params.firmId,
                    });

                const response =
                    await api.get(
                        `${config.getSuppliers}${queryString}`
                    );

                return {
                    records:
                        getRecords(
                            response
                        ),

                    pagination:
                        getPagination(
                            response
                        ),

                    summary:
                        getSummary(
                            response
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load suppliers."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH ACTIVE SUPPLIERS
|--------------------------------------------------------------------------
|
| Used by:
|
| Purchase Bill
| Payment Out
| Supplier Ledger
|--------------------------------------------------------------------------
*/

export const fetchActiveSuppliers =
    createAsyncThunk(
        "supplier/fetchActiveSuppliers",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        search:
                            params.search,

                        limit:
                            params.limit ||
                            500,
                    });

                const response =
                    await api.get(
                        `${config.getActiveSuppliers}${queryString}`
                    );

                /*
                |--------------------------------------------------------------------------
                | FIX:
                |
                | Active supplier endpoint may return:
                |
                | data: []
                |
                | OR
                |
                | data: {
                |     records: []
                | }
                |--------------------------------------------------------------------------
                */

                return getRecords(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load active suppliers."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH SUPPLIER BY ID
|--------------------------------------------------------------------------
*/

export const fetchSupplierById =
    createAsyncThunk(
        "supplier/fetchSupplierById",

        async (
            supplierId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                const response =
                    await api.get(
                        `${config.getSupplierById}/${supplierId}`
                    );

                const data =
                    getResponseData(
                        response
                    );

                return (
                    data?.supplier ||
                    data ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| CREATE SUPPLIER
|--------------------------------------------------------------------------
*/

export const createSupplier =
    createAsyncThunk(
        "supplier/createSupplier",

        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !payload
                        ?.supplierName
                        ?.trim()
                ) {
                    return rejectWithValue(
                        "Supplier name is required."
                    );
                }

                if (
                    !payload
                        ?.mobile
                        ?.trim()
                ) {
                    return rejectWithValue(
                        "Supplier mobile number is required."
                    );
                }

                const response =
                    await api.post(
                        config.createSupplier,
                        payload
                    );

                const data =
                    getResponseData(
                        response
                    );

                return (
                    data?.supplier ||
                    data ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to create supplier."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| UPDATE SUPPLIER
|--------------------------------------------------------------------------
*/

export const updateSupplier =
    createAsyncThunk(
        "supplier/updateSupplier",

        async (
            {
                supplierId,
                payload,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                const response =
                    await api.put(
                        `${config.updateSupplier}/${supplierId}`,
                        payload
                    );

                const data =
                    getResponseData(
                        response
                    );

                return (
                    data?.supplier ||
                    data ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to update supplier."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| TOGGLE SUPPLIER STATUS
|--------------------------------------------------------------------------
*/

export const toggleSupplierStatus =
    createAsyncThunk(
        "supplier/toggleSupplierStatus",

        async (
            supplierId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                const response =
                    await api.patch(
                        `${config.toggleSupplierStatus}/${supplierId}/toggle-status`,
                        {}
                    );

                const data =
                    getResponseData(
                        response
                    );

                return (
                    data?.supplier ||
                    data ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to change supplier status."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| SET SUPPLIER STATUS
|--------------------------------------------------------------------------
*/

export const setSupplierStatus =
    createAsyncThunk(
        "supplier/setSupplierStatus",

        async (
            {
                supplierId,
                status,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                if (
                    !status
                ) {
                    return rejectWithValue(
                        "Supplier status is required."
                    );
                }

                const response =
                    await api.patch(
                        `${config.updateSupplierStatus}/${supplierId}/status`,
                        {
                            status,
                        }
                    );

                const data =
                    getResponseData(
                        response
                    );

                return (
                    data?.supplier ||
                    data ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to update supplier status."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| DELETE SUPPLIER
|--------------------------------------------------------------------------
*/

export const deleteSupplier =
    createAsyncThunk(
        "supplier/deleteSupplier",

        async (
            supplierId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                const response =
                    await api.delete(
                        `${config.deleteSupplier}/${supplierId}`
                    );

                return {
                    supplierId,

                    data:
                        getResponseData(
                            response
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to delete supplier."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    /*
    |--------------------------------------------------------------------------
    | LIST
    |--------------------------------------------------------------------------
    */

    records: [],

    /*
    |--------------------------------------------------------------------------
    | ACTIVE DROPDOWN
    |--------------------------------------------------------------------------
    */

    activeSuppliers:
        [],

    /*
    |--------------------------------------------------------------------------
    | SELECTED
    |--------------------------------------------------------------------------
    */

    selectedSupplier:
        null,

    /*
    |--------------------------------------------------------------------------
    | CREATE / UPDATE
    |--------------------------------------------------------------------------
    */

    createdSupplier:
        null,

    updatedSupplier:
        null,

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    pagination: {
        ...DEFAULT_PAGINATION,
    },

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    summary: {
        ...DEFAULT_SUMMARY,
    },

    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    loading: {
        list:
            false,

        active:
            false,

        details:
            false,

        create:
            false,

        update:
            false,

        toggleStatus:
            false,

        setStatus:
            false,

        delete:
            false,
    },

    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    error: {
        list:
            null,

        active:
            null,

        details:
            null,

        create:
            null,

        update:
            null,

        toggleStatus:
            null,

        setStatus:
            null,

        delete:
            null,
    },
};


/*
|--------------------------------------------------------------------------
| REPLACE RECORD HELPER
|--------------------------------------------------------------------------
*/

const replaceRecord = (
    records,
    updatedRecord
) => {
    if (
        !Array.isArray(
            records
        ) ||
        !updatedRecord
            ?._id
    ) {
        return records;
    }

    return records.map(
        (
            record
        ) =>
            String(
                record
                    ?._id ||
                ""
            ) ===
            String(
                updatedRecord
                    ._id
            )
                ? updatedRecord
                : record
    );
};


/*
|--------------------------------------------------------------------------
| UPSERT ACTIVE SUPPLIER
|--------------------------------------------------------------------------
*/

const updateActiveSupplierList =
    (
        state,
        supplier
    ) => {
        if (
            !supplier?._id
        ) {
            return;
        }

        const supplierId =
            String(
                supplier._id
            );

        /*
        |--------------------------------------------------------------------------
        | ACTIVE
        |--------------------------------------------------------------------------
        */

        if (
            String(
                supplier.status ||
                ""
            ).toUpperCase() ===
            "ACTIVE"
        ) {
            const exists =
                state
                    .activeSuppliers
                    .some(
                        (
                            item
                        ) =>
                            String(
                                item
                                    ?._id ||
                                ""
                            ) ===
                            supplierId
                    );

            if (
                exists
            ) {
                state.activeSuppliers =
                    replaceRecord(
                        state.activeSuppliers,
                        supplier
                    );
            } else {
                state.activeSuppliers.push(
                    supplier
                );
            }

            return;
        }

        /*
        |--------------------------------------------------------------------------
        | INACTIVE
        |--------------------------------------------------------------------------
        */

        state.activeSuppliers =
            state.activeSuppliers.filter(
                (
                    item
                ) =>
                    String(
                        item?._id ||
                        ""
                    ) !==
                    supplierId
            );
    };


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const supplierSlice =
    createSlice({
        name:
            "supplier",

        initialState,

        reducers: {
            /*
            |--------------------------------------------------------------------------
            | CLEAR SELECTED
            |--------------------------------------------------------------------------
            */

            clearSelectedSupplier: (
                state
            ) => {
                state.selectedSupplier =
                    null;

                state.error.details =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR CREATED
            |--------------------------------------------------------------------------
            */

            clearCreatedSupplier: (
                state
            ) => {
                state.createdSupplier =
                    null;

                state.error.create =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR UPDATED
            |--------------------------------------------------------------------------
            */

            clearUpdatedSupplier: (
                state
            ) => {
                state.updatedSupplier =
                    null;

                state.error.update =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ACTIVE
            |--------------------------------------------------------------------------
            */

            clearActiveSuppliers: (
                state
            ) => {
                state.activeSuppliers =
                    [];

                state.error.active =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ERRORS
            |--------------------------------------------------------------------------
            */

            clearSupplierErrors: (
                state
            ) => {
                state.error = {
                    list:
                        null,

                    active:
                        null,

                    details:
                        null,

                    create:
                        null,

                    update:
                        null,

                    toggleStatus:
                        null,

                    setStatus:
                        null,

                    delete:
                        null,
                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET
            |--------------------------------------------------------------------------
            */

            resetSupplierState:
                () => ({
                    ...initialState,

                    records:
                        [],

                    activeSuppliers:
                        [],

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },

                    summary: {
                        ...DEFAULT_SUMMARY,
                    },

                    loading: {
                        ...initialState
                            .loading,
                    },

                    error: {
                        ...initialState
                            .error,
                    },
                }),
        },


        /*
        |--------------------------------------------------------------------------
        | EXTRA REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | FETCH LIST
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSuppliers
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.list =
                            true;

                        state.error.list =
                            null;
                    }
                )

                .addCase(
                    fetchSuppliers
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.records =
                            Array.isArray(
                                action
                                    .payload
                                    ?.records
                            )
                                ? action
                                    .payload
                                    .records
                                : [];

                        /*
                        |--------------------------------------------------------------------------
                        | FIXED PAGINATION
                        |--------------------------------------------------------------------------
                        */

                        state.pagination = {
                            ...DEFAULT_PAGINATION,

                            ...(action
                                .payload
                                ?.pagination ||
                                {}),
                        };

                        /*
                        |--------------------------------------------------------------------------
                        | FIXED SUMMARY
                        |--------------------------------------------------------------------------
                        */

                        state.summary = {
                            ...DEFAULT_SUMMARY,

                            ...(action
                                .payload
                                ?.summary ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchSuppliers
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.error.list =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load suppliers.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | ACTIVE SUPPLIERS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchActiveSuppliers
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.active =
                            true;

                        state.error.active =
                            null;
                    }
                )

                .addCase(
                    fetchActiveSuppliers
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.active =
                            false;

                        state.activeSuppliers =
                            Array.isArray(
                                action.payload
                            )
                                ? action.payload
                                : [];
                    }
                )

                .addCase(
                    fetchActiveSuppliers
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.active =
                            false;

                        state.activeSuppliers =
                            [];

                        state.error.active =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load active suppliers.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | DETAILS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierById
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.details =
                            true;

                        state.error.details =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierById
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.selectedSupplier =
                            action.payload ||
                            null;
                    }
                )

                .addCase(
                    fetchSupplierById
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.error.details =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | CREATE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    createSupplier
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.create =
                            true;

                        state.error.create =
                            null;

                        state.createdSupplier =
                            null;
                    }
                )

                .addCase(
                    createSupplier
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        state.createdSupplier =
                            action.payload ||
                            null;

                        state.selectedSupplier =
                            action.payload ||
                            null;

                        if (
                            !action.payload
                                ?._id
                        ) {
                            return;
                        }

                        const supplier =
                            action.payload;

                        /*
                        |--------------------------------------------------------------------------
                        | ADD TO LIST
                        |--------------------------------------------------------------------------
                        */

                        state.records =
                        [
                            supplier,

                            ...state.records.filter(
                                (
                                    record
                                ) =>
                                    String(
                                        record
                                            ?._id ||
                                        ""
                                    ) !==
                                    String(
                                        supplier._id
                                    )
                            ),
                        ];

                        updateActiveSupplierList(
                            state,
                            supplier
                        );

                        /*
                        |--------------------------------------------------------------------------
                        | OPTIMISTIC PAGINATION
                        |--------------------------------------------------------------------------
                        */

                        state.pagination.total =
                            Number(
                                state
                                    .pagination
                                    .total ||
                                0
                            ) + 1;
                    }
                )

                .addCase(
                    createSupplier
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        state.error.create =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to create supplier.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | UPDATE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    updateSupplier
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.update =
                            true;

                        state.error.update =
                            null;

                        state.updatedSupplier =
                            null;
                    }
                )

                .addCase(
                    updateSupplier
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.update =
                            false;

                        state.updatedSupplier =
                            action.payload ||
                            null;

                        state.selectedSupplier =
                            action.payload ||
                            state
                                .selectedSupplier;

                        state.records =
                            replaceRecord(
                                state.records,
                                action.payload
                            );

                        updateActiveSupplierList(
                            state,
                            action.payload
                        );
                    }
                )

                .addCase(
                    updateSupplier
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.update =
                            false;

                        state.error.update =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to update supplier.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | TOGGLE STATUS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    toggleSupplierStatus
                        .pending,
                    (
                        state
                    ) => {
                        state.loading
                            .toggleStatus =
                            true;

                        state.error
                            .toggleStatus =
                            null;
                    }
                )

                .addCase(
                    toggleSupplierStatus
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .toggleStatus =
                            false;

                        const supplier =
                            action.payload;

                        if (
                            !supplier?._id
                        ) {
                            return;
                        }

                        state.records =
                            replaceRecord(
                                state.records,
                                supplier
                            );

                        if (
                            String(
                                state
                                    .selectedSupplier
                                    ?._id ||
                                ""
                            ) ===
                            String(
                                supplier._id
                            )
                        ) {
                            state.selectedSupplier =
                                supplier;
                        }

                        updateActiveSupplierList(
                            state,
                            supplier
                        );
                    }
                )

                .addCase(
                    toggleSupplierStatus
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .toggleStatus =
                            false;

                        state.error
                            .toggleStatus =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to change supplier status.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | SET STATUS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    setSupplierStatus
                        .pending,
                    (
                        state
                    ) => {
                        state.loading
                            .setStatus =
                            true;

                        state.error
                            .setStatus =
                            null;
                    }
                )

                .addCase(
                    setSupplierStatus
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .setStatus =
                            false;

                        const supplier =
                            action.payload;

                        if (
                            !supplier?._id
                        ) {
                            return;
                        }

                        state.records =
                            replaceRecord(
                                state.records,
                                supplier
                            );

                        if (
                            String(
                                state
                                    .selectedSupplier
                                    ?._id ||
                                ""
                            ) ===
                            String(
                                supplier._id
                            )
                        ) {
                            state.selectedSupplier =
                                supplier;
                        }

                        updateActiveSupplierList(
                            state,
                            supplier
                        );
                    }
                )

                .addCase(
                    setSupplierStatus
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .setStatus =
                            false;

                        state.error
                            .setStatus =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to update supplier status.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | DELETE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    deleteSupplier
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.delete =
                            true;

                        state.error.delete =
                            null;
                    }
                )

                .addCase(
                    deleteSupplier
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.delete =
                            false;

                        const supplierId =
                            action
                                .payload
                                ?.supplierId;

                        if (
                            !supplierId
                        ) {
                            return;
                        }

                        /*
                        |--------------------------------------------------------------------------
                        | REMOVE FROM MAIN LIST
                        |--------------------------------------------------------------------------
                        */

                        state.records =
                            state.records.filter(
                                (
                                    supplier
                                ) =>
                                    String(
                                        supplier
                                            ?._id ||
                                        ""
                                    ) !==
                                    String(
                                        supplierId
                                    )
                            );

                        /*
                        |--------------------------------------------------------------------------
                        | REMOVE FROM ACTIVE LIST
                        |--------------------------------------------------------------------------
                        */

                        state.activeSuppliers =
                            state.activeSuppliers.filter(
                                (
                                    supplier
                                ) =>
                                    String(
                                        supplier
                                            ?._id ||
                                        ""
                                    ) !==
                                    String(
                                        supplierId
                                    )
                            );

                        /*
                        |--------------------------------------------------------------------------
                        | CLEAR SELECTED
                        |--------------------------------------------------------------------------
                        */

                        if (
                            String(
                                state
                                    .selectedSupplier
                                    ?._id ||
                                ""
                            ) ===
                            String(
                                supplierId
                            )
                        ) {
                            state.selectedSupplier =
                                null;
                        }

                        /*
                        |--------------------------------------------------------------------------
                        | PAGINATION
                        |--------------------------------------------------------------------------
                        */

                        state.pagination.total =
                            Math.max(
                                Number(
                                    state
                                        .pagination
                                        .total ||
                                    0
                                ) - 1,
                                0
                            );
                    }
                )

                .addCase(
                    deleteSupplier
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.delete =
                            false;

                        state.error.delete =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to delete supplier.";
                    }
                );
        },
    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearSelectedSupplier,

    clearCreatedSupplier,

    clearUpdatedSupplier,

    clearActiveSuppliers,

    clearSupplierErrors,

    resetSupplierState,
} =
    supplierSlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
|
| Root reducer:
|
| supplier: supplierReducer
|--------------------------------------------------------------------------
*/

export const supplierStateSelector = (
    state
) =>
    state?.supplier ||
    initialState;


export const supplierListSelector = (
    state
) =>
    state?.supplier
        ?.records ||
    [];


export const activeSupplierListSelector = (
    state
) =>
    state?.supplier
        ?.activeSuppliers ||
    [];


export const supplierPaginationSelector = (
    state
) =>
    state?.supplier
        ?.pagination ||
    initialState.pagination;


export const supplierSummarySelector = (
    state
) =>
    state?.supplier
        ?.summary ||
    initialState.summary;


export const selectedSupplierSelector = (
    state
) =>
    state?.supplier
        ?.selectedSupplier ||
    null;


export const createdSupplierSelector = (
    state
) =>
    state?.supplier
        ?.createdSupplier ||
    null;


export const updatedSupplierSelector = (
    state
) =>
    state?.supplier
        ?.updatedSupplier ||
    null;


export const supplierLoadingSelector = (
    state
) =>
    state?.supplier
        ?.loading ||
    initialState.loading;


export const supplierErrorSelector = (
    state
) =>
    state?.supplier
        ?.error ||
    initialState.error;


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default supplierSlice.reducer;   